import {Component, Input, OnInit, inject, signal} from '@angular/core';
import {DatePipe} from '@angular/common';
import {RecipeRatingService} from '../../services/recipe/recipe-rating.service';
import {RatingListItem} from '../../services/recipe/recipe.service';
import {ProxyImageDirective} from '../../directives/proxy-image.directive';
import {UiStarBarComponent} from '../../shared/ui';
import {AppLogger, AppLoggerService} from '../../core/logging';

/**
 * Lazy comment view: the full list of everyone's ratings and notes.
 *
 * Deliberately mounted only on expand — comment bodies are the heaviest part of
 * the feature and most recipe opens never touch them. Fetches on init, so the
 * parent controls loading simply by adding/removing this component.
 *
 * Ratings with a score but no comment are still rows: they count and show
 * stars, they just have no body.
 */
@Component({
  selector: 'app-rating-list',
  standalone: true,
  imports: [DatePipe, ProxyImageDirective, UiStarBarComponent],
  template: `
    @if (isLoading()) {
      <div class="flex items-center gap-3 py-6 text-base-content/60">
        <span class="loading loading-spinner loading-sm"></span>
        <span>Loading ratings…</span>
      </div>
    } @else if (errorMessage()) {
      <div class="alert alert-error" role="alert">{{ errorMessage() }}</div>
    } @else if (ratings().length === 0) {
      <div class="text-center py-8 text-base-content/60">
        No ratings yet — be the first.
      </div>
    } @else {
      @for (r of ratings(); track r.user_id) {
        <div class="flex gap-3 py-3 border-b border-base-200 last:border-b-0">
          <div class="avatar shrink-0">
            <div class="w-9 h-9 rounded-full bg-base-200 text-base-content/60 flex items-center justify-center overflow-hidden">
              @if (r.picture_url) {
                <img [src]="r.picture_url" [alt]="r.user_name" class="w-full h-full object-cover" useImageProxy/>
              } @else {
                <span class="text-sm font-medium">{{ initials(r.user_name) }}</span>
              }
            </div>
          </div>

          <div class="flex-1 min-w-0">
            <div class="flex items-center flex-wrap gap-x-2 gap-y-0.5">
              <span class="font-medium truncate">{{ r.user_name }}</span>
              <ui-star-bar [avg]="r.score" [showValue]="false" size="sm"/>
              <span class="text-xs text-base-content/50">{{ r.updated_at | date:'mediumDate' }}</span>
            </div>
            @if (r.comment) {
              <p class="text-sm mt-1 wrap-break-word">{{ r.comment }}</p>
            }
          </div>
        </div>
      }
    }
  `,
})
export class RatingListComponent implements OnInit {
  @Input({required: true}) recipeId!: number;

  readonly ratings = signal<RatingListItem[]>([]);
  readonly isLoading = signal<boolean>(false);
  readonly errorMessage = signal<string | null>(null);

  private ratingService = inject(RecipeRatingService);
  private readonly log: AppLogger;

  constructor(appLogger: AppLoggerService) {
    this.log = appLogger.createForClass(RatingListComponent);
  }

  ngOnInit(): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.ratingService.list(this.recipeId).subscribe({
      next: (rows) => {
        this.ratings.set(rows ?? []);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.errorMessage.set('Could not load ratings.');
        this.log.error('Error loading recipe ratings', err, {recipeId: this.recipeId});
      }
    });
  }

  initials(name: string): string {
    const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }
}
